import { useEventInfo } from '@shared/utils/eventInfo';
import type { Maybe } from '@graphql/generated';
import { useJoyPlaceIdBySourceIdQuery, useGetUserRatingFromHotelByIdQuery } from '@graphql/generated';
import { addError } from '@shared/utils/logger';
import { useTransformRatingToCategory } from '@shared/hooks/useTransformRatingToCategory';
import type { HotelArgs } from '@shared/components/HotelRatingV2/HotelRatingV2.types';

const MIN_RATING = 0;
const MAX_RATING = 10;

export type HotelRatingContext = 'admin-hotel-form' | 'admin-hotel-pdp-dialog' | 'admin-hotels-list-item' | 'admin-sortable-card' | 'stays-property-information';

interface UseHotelUserRatingResult
  extends Readonly<{
    reviewCount?: Maybe<number>;
    validatedRating: number;
    ratingCategory: string;
    loading: boolean;
  }> {}

/**
 * Validates and clamps a rating value to the expected 0-10 scale.
 * Rounds to at most one decimal place.
 * Logs an error if the value is outside the valid range.
 */
const validateAndClampRating = (context: HotelRatingContext, ratingValue?: Maybe<number>): number => {
  if (ratingValue == null) return 0;

  const roundedValue = Math.round(ratingValue * 10) / 10;

  if (roundedValue < MIN_RATING || roundedValue > MAX_RATING) {
    const clampedValue = Math.max(MIN_RATING, Math.min(MAX_RATING, roundedValue));

    addError('Invalid Rating Value', {
      originalValue: ratingValue,
      roundedValue,
      clampedValue,
      validRange: `${MIN_RATING}-${MAX_RATING}`,
      location: context,
    });

    return clampedValue;
  }

  return roundedValue;
};

interface UseHotelUserRatingProps
  extends Readonly<{
    hotel: HotelArgs;
    context: HotelRatingContext;
    skip?: boolean;
  }> {}

export const useHotelUserRating = ({ hotel, context, skip }: UseHotelUserRatingProps): UseHotelUserRatingResult => {
  const { eventInfo, loading: loadingEvent } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const shouldQueryJoyPlace = Boolean(eventId && hotel.zhId && !hotel.joyPlaceId);

  const { data: joyPlaceData, loading: loadingJoyPlace } = useJoyPlaceIdBySourceIdQuery({
    batchMode: 'fast',
    variables: {
      payload: {
        eventId: eventId!,
        sourceId: hotel.zhId!,
        sourceType: 'zh',
      },
    },
    skip: skip || !shouldQueryJoyPlace,
  });

  /**
   * Callers (HotelPdpDialog, HotelForm, PropertyInformation, HotelsListItemV2, SortableCard)
   * pass catalog UUIDs for itemId/id, or omit them entirely (falling back to joyPlaceId).
   * UUID validation was removed because no caller passes source platform IDs here.
   * If you're adding a new caller, ensure itemId/id is a catalog UUID, NOT a source ID (zhId).
   */
  const accommodationPlaceId = hotel.itemId || hotel.id;
  const resolvedJoyPlaceId = hotel.joyPlaceId || joyPlaceData?.joyPlaceBySourceId?.id;

  const idObject = accommodationPlaceId ? { accommodationPlaceId } : resolvedJoyPlaceId ? { joyPlaceId: resolvedJoyPlaceId } : null;

  const { data: ratingData, loading: loadingRating } = useGetUserRatingFromHotelByIdQuery({
    batchMode: 'fast',
    variables: {
      idObject: idObject!,
    },
    skip: skip || !idObject,
  });

  const loading = loadingEvent || loadingJoyPlace || loadingRating;
  const userRating = ratingData?.accommodationCatalogPropertyById?.userRating;

  const validatedRating = validateAndClampRating(context, userRating?.ratingValue);

  const ratingCategory = useTransformRatingToCategory(validatedRating);

  return {
    reviewCount: userRating?.ratingCount,
    validatedRating,
    ratingCategory,
    loading,
  };
};
